import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Images, ArrowLeft, Folder, Layers } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { PublicHeader } from "@/components/PublicHeader";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import { pickLang } from "@/lib/format";

/**
 * Public Gallery page.
 *
 * View states:
 *   - "grid"  : top-level "All Photos" tile + one tile per album.
 *   - "album" : drill-down into a single album (or the synthetic "All").
 *
 * The synthetic "All Photos" tile is *not* a real album — it's a client-side
 * pseudo-album whose id is the sentinel string ``__all__`` and whose photos
 * are the full unfiltered list. This keeps a single drill-down component
 * responsible for rendering photos regardless of whether the user tapped a
 * real album or the "all" tile.
 */

const ALL_ID = "__all__";

function formatCount(n, tSection) {
  const template = n === 1 ? tSection.photoCount : (tSection.photoCountPlural || tSection.photoCount);
  return (template || "{n}").replace("{n}", String(n));
}

export default function GalleryPage() {
  const { t, lang } = useI18n();
  const tGallery = t.gallery;

  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [active, setActive] = useState(null); // photo doc for the lightbox
  const [openAlbumId, setOpenAlbumId] = useState(null); // null = grid view

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, a] = await Promise.all([api.get("/photos"), api.get("/albums")]);
        if (!cancelled) {
          setPhotos(p.data || []);
          setAlbums(a.data || []);
        }
      } catch (_) {
        // Fail quietly — the empty state below covers this.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The drill-down album (null if we're on the top-level grid).
  const openAlbum = useMemo(() => {
    if (!openAlbumId) return null;
    if (openAlbumId === ALL_ID) {
      return {
        id: ALL_ID,
        name_es: tGallery.allPhotos,
        name_en: tGallery.allPhotos,
        description_es: tGallery.allPhotosSubtitle,
        description_en: tGallery.allPhotosSubtitle,
        photo_count: photos.length,
        is_synthetic: true,
      };
    }
    return albums.find((a) => a.id === openAlbumId) || null;
  }, [openAlbumId, albums, photos.length, tGallery.allPhotos, tGallery.allPhotosSubtitle]);

  // Photos to render in the drill-down (either all, or filtered to one album).
  const openAlbumPhotos = useMemo(() => {
    if (!openAlbumId) return [];
    if (openAlbumId === ALL_ID) return photos;
    return photos.filter((p) => p.album_id === openAlbumId);
  }, [openAlbumId, photos]);

  // Best-effort cover for the "All Photos" synthetic tile: use the newest
  // photo overall so the tile isn't a boring placeholder.
  const allPhotosCoverUrl = photos[0]?.image_url || null;

  const isEmpty = !loading && photos.length === 0 && albums.length <= 1;

  return (
    <div>
      <PublicHeader />
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className="px-4 pt-4 space-y-4"
      >
        {/* -------------------- Page title / back button -------------------- */}
        {openAlbum ? (
          <div className="flex items-start gap-2 px-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpenAlbumId(null)}
              className="-ml-2 h-8 rounded-full"
              data-testid="gallery-back-to-albums"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              {tGallery.backToAlbums}
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-1">
            <Images className="h-5 w-5 text-primary" />
            <h1
              className="font-serif-display text-2xl font-semibold tracking-tight"
              data-testid="gallery-page-title"
            >
              {tGallery.title}
            </h1>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-2 gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="aspect-square rounded-xl" />
            ))}
          </div>
        ) : isEmpty ? (
          <Card className="rounded-2xl border-dashed bg-white/60">
            <CardContent
              className="py-12 text-center text-sm text-muted-foreground"
              data-testid="gallery-empty"
            >
              {tGallery.empty}
            </CardContent>
          </Card>
        ) : (
          <AnimatePresence mode="wait" initial={false}>
            {openAlbum ? (
              /* ==================== ALBUM DRILL-DOWN ==================== */
              <motion.div
                key={`album-${openAlbum.id}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18 }}
                className="space-y-4"
                data-testid={`gallery-album-view-${openAlbum.id}`}
              >
                <div className="px-1">
                  <h2 className="font-serif-display text-xl font-semibold tracking-tight">
                    {lang === "es" ? openAlbum.name_es : openAlbum.name_en}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {formatCount(openAlbumPhotos.length, tGallery)}
                  </p>
                  {(lang === "es" ? openAlbum.description_es : openAlbum.description_en) && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {lang === "es" ? openAlbum.description_es : openAlbum.description_en}
                    </p>
                  )}
                </div>

                {openAlbumPhotos.length === 0 ? (
                  <Card className="rounded-2xl border-dashed bg-white/60">
                    <CardContent
                      className="py-10 text-center text-sm text-muted-foreground"
                      data-testid="gallery-empty-album"
                    >
                      {tGallery.emptyAlbum}
                    </CardContent>
                  </Card>
                ) : (
                  <div
                    className="grid grid-cols-2 gap-2 sm:grid-cols-3"
                    data-testid="gallery-grid"
                  >
                    {openAlbumPhotos.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => setActive(p)}
                        className="aspect-square overflow-hidden rounded-xl bg-muted ring-1 ring-black/5 active:scale-[0.99] transition-transform"
                        data-testid="gallery-photo-tile"
                        aria-label={pickLang(p, "caption", lang) || "photo"}
                      >
                        <img
                          src={p.image_url}
                          alt={pickLang(p, "caption", lang) || ""}
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            ) : (
              /* ==================== TOP-LEVEL ALBUM GRID ==================== */
              <motion.div
                key="albums-grid"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18 }}
                className="space-y-3"
              >
                <p className="px-1 text-xs uppercase tracking-[0.16em] text-muted-foreground">
                  {tGallery.albumsHeading}
                </p>
                <div
                  className="grid grid-cols-2 gap-3 sm:grid-cols-3"
                  data-testid="gallery-albums-grid"
                >
                  {/* --- All photos synthetic tile --- */}
                  <button
                    onClick={() => setOpenAlbumId(ALL_ID)}
                    className="group relative aspect-square overflow-hidden rounded-2xl bg-muted ring-1 ring-black/5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-transform active:scale-[0.99]"
                    data-testid="gallery-album-tile-all"
                  >
                    {allPhotosCoverUrl ? (
                      <img
                        src={allPhotosCoverUrl}
                        alt=""
                        className="absolute inset-0 h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/25 via-primary/10 to-transparent">
                        <Layers className="h-10 w-10 text-primary/70" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
                    <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-white/85 px-2 py-0.5 text-[9px] font-medium uppercase tracking-wider text-primary shadow-sm backdrop-blur">
                      <Layers className="h-3 w-3" />
                      {tGallery.allPhotosSubtitle}
                    </span>
                    <div className="absolute inset-x-0 bottom-0 p-3 text-white">
                      <p className="font-serif-display text-base font-semibold leading-tight">
                        {tGallery.allPhotos}
                      </p>
                      <p className="text-[11px] text-white/85">
                        {formatCount(photos.length, tGallery)}
                      </p>
                    </div>
                  </button>

                  {/* --- Real album tiles --- */}
                  {albums.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => setOpenAlbumId(a.id)}
                      className="group relative aspect-square overflow-hidden rounded-2xl bg-muted ring-1 ring-black/5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-transform active:scale-[0.99]"
                      data-testid={`gallery-album-tile-${a.id}`}
                    >
                      {a.cover_url ? (
                        <img
                          src={a.cover_url}
                          alt=""
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-muted">
                          <Folder className="h-10 w-10 text-muted-foreground/50" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 p-3 text-white">
                        <p className="font-serif-display text-base font-semibold leading-tight line-clamp-2">
                          {lang === "es" ? a.name_es : a.name_en}
                        </p>
                        <p className="text-[11px] text-white/85">
                          {formatCount(a.photo_count || 0, tGallery)}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {/* -------------------- Lightbox modal (shared) -------------------- */}
        <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
          <DialogContent className="max-w-md p-0 bg-background">
            <DialogTitle className="sr-only">Photo</DialogTitle>
            {active && (
              <div className="flex flex-col">
                <img
                  src={active.image_url}
                  alt=""
                  className="w-full max-h-[70vh] object-contain bg-black/5"
                />
                {pickLang(active, "caption", lang) && (
                  <p className="p-4 text-sm text-muted-foreground">
                    {pickLang(active, "caption", lang)}
                  </p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <div className="h-2" />
      </motion.div>
    </div>
  );
}
